// CRUD admin de profesores_tarifas — €/clase por combo profesor+tipo_clase.
// Tabla con todas las tarifas (activas + inactivas en modo "ver historial") + modal alta/edición.

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Trash2, AlertCircle, Loader2, X, Check } from 'lucide-react';
import { createPortal } from 'react-dom';

import {
  listProfesorTarifas, createProfesorTarifa, updateProfesorTarifa, deleteProfesorTarifa,
  listProfesores, listClasesTipos,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

function fmtPrecio(n) {
  return Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const FORM_VACIO = { profesor_id: '', clase_tipo_id: '', precio_clase: '', activa: true };

export default function AdminProfesorTarifas() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [clasesTipos, setClasesTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);
  const [verInactivas, setVerInactivas] = useState(false);
  const [filtroProf, setFiltroProf] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const filtros = {};
      if (!verInactivas) filtros.activa = 'true';
      if (filtroProf) filtros.profesor_id = filtroProf;
      const [r, profs, clases] = await Promise.all([
        listProfesorTarifas(filtros),
        listProfesores(),
        listClasesTipos(),
      ]);
      setItems(r.data || []);
      setProfesores(profs.data || []);
      setClasesTipos(clases.data || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [verInactivas, filtroProf]);
  useEffect(() => { cargar(); }, [cargar]);

  const onBorrar = async (item) => {
    const nombre = item.profesor?.nombre_publico || '';
    const clase = item.clase_tipo?.nombre_es || '';
    if (!window.confirm(t('admin.profesor_tarifas.confirmar_desactivar', { nombre, clase }))) return;
    try {
      await deleteProfesorTarifa(item.id);
      cargar();
    } catch (e) { alert(e.message || t('admin.error_generico')); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.profesor_tarifas.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.profesor_tarifas.sub')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditar({ ...FORM_VACIO })}>
          <Plus size={16} strokeWidth={1.75} /> {t('admin.crear_nuevo')}
        </button>
      </header>

      <div className="admin-filtros admin-filtros-inline">
        <label className="admin-filtro-campo">
          <span>{t('admin.profesor_tarifas.filtro_profesor')}</span>
          <select value={filtroProf} onChange={(e) => setFiltroProf(e.target.value)}>
            <option value="">{t('admin.profesor_tarifas.todos_profesores')}</option>
            {profesores.map((p) => (
              <option key={p.id} value={p.id}>{p.nombre_publico}</option>
            ))}
          </select>
        </label>
        <label className="admin-filtro-toggle">
          <input
            type="checkbox"
            checked={verInactivas}
            onChange={(e) => setVerInactivas(e.target.checked)}
          />
          <span>{t('admin.profesor_tarifas.ver_inactivas')}</span>
        </label>
      </div>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}

      {!loading && !error && items.length === 0 && (
        <div className="admin-empty">{t('admin.profesor_tarifas.vacio')}</div>
      )}

      {!loading && !error && items.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.profesor_tarifas.col.profesor')}</th>
              <th>{t('admin.profesor_tarifas.col.clase')}</th>
              <th>{t('admin.profesor_tarifas.col.precio')}</th>
              <th>{t('admin.profesor_tarifas.col.estado')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className={it.activa ? '' : 'admin-row-inactiva'}>
                <td>{it.profesor?.nombre_publico || '—'}</td>
                <td>{it.clase_tipo?.nombre_es || '—'}</td>
                <td className="num">{fmtPrecio(it.precio_clase)} €</td>
                <td>
                  {it.activa
                    ? <span className="estado-pill estado-pill-ok"><Check size={12} /> {t('admin.profesor_tarifas.activa')}</span>
                    : <span className="estado-pill">{t('admin.profesor_tarifas.inactiva')}</span>}
                </td>
                <td className="admin-acciones">
                  <button type="button" onClick={() => setEditar({ ...it })} aria-label={t('admin.editar')}>
                    <Edit2 size={16} strokeWidth={1.5} />
                  </button>
                  {it.activa && (
                    <button type="button" onClick={() => onBorrar(it)} aria-label={t('admin.borrar')}>
                      <Trash2 size={16} strokeWidth={1.5} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editar && (
        <TarifaModal
          item={editar}
          profesores={profesores}
          clasesTipos={clasesTipos}
          onCerrar={() => setEditar(null)}
          onGuardado={() => { setEditar(null); cargar(); }}
        />
      )}
    </div>
  );
}

function TarifaModal({ item, profesores, clasesTipos, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const editando = !!item.id;
  const [form, setForm] = useState({
    profesor_id: item.profesor_id || '',
    clase_tipo_id: item.clase_tipo_id || '',
    precio_clase: item.precio_clase != null ? String(item.precio_clase) : '',
    activa: item.activa !== false,
  });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const setCampo = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setEnviando(true);
    try {
      const precio = Number(form.precio_clase);
      if (!Number.isFinite(precio) || precio <= 0) {
        throw new Error(t('admin.profesor_tarifas.err_precio'));
      }
      if (editando) {
        await updateProfesorTarifa(item.id, {
          precio_clase: precio,
          activa: !!form.activa,
        });
      } else {
        if (!form.profesor_id) throw new Error(t('admin.profesor_tarifas.err_profesor'));
        if (!form.clase_tipo_id) throw new Error(t('admin.profesor_tarifas.err_clase'));
        await createProfesorTarifa({
          profesor_id: form.profesor_id,
          clase_tipo_id: form.clase_tipo_id,
          precio_clase: precio,
          activa: !!form.activa,
        });
      }
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e.message || t('admin.error_generico')));
    } finally { setEnviando(false); }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">
          {editando ? t('admin.profesor_tarifas.titulo_editar') : t('admin.profesor_tarifas.titulo_crear')}
        </h2>

        <form className="modal-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <div className="modal-campo">
              <label>{t('admin.profesor_tarifas.f.profesor')}</label>
              <select
                value={form.profesor_id}
                onChange={setCampo('profesor_id')}
                disabled={editando}
                required
              >
                <option value="">—</option>
                {profesores.filter((p) => p.activo).map((p) => (
                  <option key={p.id} value={p.id}>{p.nombre_publico}</option>
                ))}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.profesor_tarifas.f.clase')}</label>
              <select
                value={form.clase_tipo_id}
                onChange={setCampo('clase_tipo_id')}
                disabled={editando}
                required
              >
                <option value="">—</option>
                {clasesTipos.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre_es}</option>
                ))}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.profesor_tarifas.f.precio')}</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={form.precio_clase}
                onChange={setCampo('precio_clase')}
                placeholder="25.00"
                required
              />
            </div>
            <div className="modal-campo">
              <label className="admin-filtro-toggle" style={{ marginTop: '24px' }}>
                <input type="checkbox" checked={form.activa} onChange={setCampo('activa')} />
                <span>{t('admin.profesor_tarifas.f.activa')}</span>
              </label>
            </div>
          </div>

          {editando && (
            <p className="admin-form-hint">{t('admin.profesor_tarifas.hint_editar')}</p>
          )}
          {!editando && (
            <p className="admin-form-hint">{t('admin.profesor_tarifas.hint_crear')}</p>
          )}

          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}

          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>
              {t('comun.cancelar')}
            </button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
